// Import necessary React hooks and components
import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import "./ClientChatBox.css";

// Import chat context and services
import { useChat } from "../../../context/ChatContext";
import {
  addEmojiReactionToMessage,
  deleteMessage,
  getMessagesByRoomId,
  openRoom,
  sendMessage,
  updateSeenStatus,
} from "../../../services/clientWebsite";

// Import Redux hooks and utilities
import { useDispatch, useSelector } from "react-redux";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import { notify } from "../../../components/Toast";
import EmojiPicker from "emoji-picker-react";
import { current } from "@reduxjs/toolkit";
import { ChatShimmer } from "../../../components/Shimmer/Shimmer";
import { toggleChatBox } from "../../../redux/slices/uiSlice";

// Define the ClientChatBox component
const ClientChatBox = () => {
  // Redux state for user and chat status
  const user = useSelector((state) => state.auth.user);
  const chatEnabled = useSelector((state) => state.auth.chatEnabled);
  const isChatBoxOpen = useSelector((state) => state.ui.isChatBoxOpen);
  const dispatch = useDispatch();

  // Local state for chat functionality
  const [roomId, setRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showInputEmojiPicker, setShowInputEmojiPicker] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [hoveredMsgId, setHoveredMsgId] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [isStartMessage, setIsStartMessage] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [reactions, setReactions] = useState({});

  // Refs for DOM elements
  const messagesEndRef = useRef(null);
  const { socketRef, sendMessage: emitSocketMessage } = useChat();
  const dropdownRef = useRef(null);
  const inputEmojiRef = useRef(null);
  const reactionPickerRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const chatBoxRef = useRef(null); // Ref for the chatbox container

  // Sync chatbox open state with Redux
  useEffect(() => {
    if (isChatBoxOpen) {
      setIsOpen(true);
    }
  }, [isChatBoxOpen]);

  // Handle clicks outside chatbox and dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if clicked outside
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setHoveredMsgId(null);
      }

      // Close reaction picker if clicked outside
      if (
        reactionPickerRef.current &&
        !reactionPickerRef.current.contains(event.target)
      ) {
        setShowReactionPicker(null);
      }

      // Close input emoji picker if clicked outside
      if (
        inputEmojiRef.current &&
        !inputEmojiRef.current.contains(event.target)
      ) {
        setShowInputEmojiPicker(false);
      }

      // Close chatbox if clicked outside and not on chat fab
      if (
        isOpen &&
        chatBoxRef.current &&
        !chatBoxRef.current.contains(event.target) &&
        !event.target.closest(".chat-fab")
      ) {
        setIsOpen(false);
        setPage(1);
        setMessages([]);
        setHasMoreMessages(true);
        setLastPage(1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle emoji reactions
  const handleEmojiReaction = async (emojiData, messageId) => {
    try {
      const currentReactions = reactions[messageId] || [];
      const emoji = emojiData.emoji;
      const alreadyReacted = currentReactions.includes(emoji);
      const action = alreadyReacted ? "remove" : "add";

      const payload = {
        emoji: alreadyReacted ? null : emoji,
        sender_id: user.id,
        action,
      };

      const response = await addEmojiReactionToMessage(payload, messageId);

      if (response.success) {
        setReactions((prev) => {
          const updatedReactions = alreadyReacted
            ? currentReactions.filter((e) => e !== emoji)
            : [emoji];
          return {
            ...prev,
            [messageId]: updatedReactions,
          };
        });

        setMessages((prevMessages) =>
          prevMessages.map((m) =>
            m.id === messageId ? { ...m, emoji: alreadyReacted ? null : emoji } : m
          )
        );
      } else {
        notify.error(response.message || "Failed to update emoji reaction");
      }
    } catch (error) {
      console.error("Emoji Reaction Error:", error);
      notify.error("Failed to update emoji reaction");
    }

    setShowReactionPicker(null);
    setHoveredMsgId(null);
  };

  // Fetch room on component mount
  useEffect(() => {
    if (user?.id) {
      fetchRoomOnInit(user.id);
    }
  }, [user]);

  // Join socket room when roomId is available
  useEffect(() => {
    if (roomId && socketRef.current) {
      socketRef.current.emit("join_room", roomId);
    }
  }, [roomId]);

  // Handle incoming socket messages
  useEffect(() => {
    if (!socketRef.current) return;

    const handleNewMessage = (msg) => {
      const roomId = msg.room_id || msg.chat_room_id;
      const currentRoom = roomId;

      const formatted = normalizeIncoming(msg);

      if (msg.room_id === currentRoom) {
        setMessages((prev) => {
          // Check if message already exists to prevent duplicates
          const messageExists = prev.some(existingMsg =>
            existingMsg.body === formatted.body &&
            existingMsg.sender_id === formatted.sender_id &&
            Math.abs(new Date(existingMsg.created_at).getTime() - new Date(formatted.created_at).getTime()) < 1000
          );

          if (messageExists) {
            return prev;
          }
          return [formatted, ...prev];
        });

        if (isOpen) {
          handleMessageSeenStatusUpdate();
        }
      }
    };

    socketRef.current.on("newMessage", handleNewMessage);

    return () => {
      socketRef.current.off("newMessage", handleNewMessage);
    };
  }, [socketRef.current, roomId, isOpen]);

  // Scroll to bottom when messages update
  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch messages when chat popup opens
  useEffect(() => {
    if (isOpen && roomId) {
      fetchMessages(roomId);
      handleMessageSeenStatusUpdate();
    }
  }, [isOpen, roomId]);

  // Update seen status for messages
  const handleMessageSeenStatusUpdate = async () => {
    if (!roomId) return;
    try {
      const data = {
        receiver_id: user.id,
      };
      const resp = await updateSeenStatus(data, roomId);
      console.log("✅ LOG: Seen status updated:", resp);
    } catch (err) {
      console.error("❌ LOG: Error updating seen status:", err);
    }
  };

  // Fetch chat room
  const fetchRoomOnInit = async (userId) => {
    setChatLoading(true);
    try {
      const chatRoomRes = await openRoom(userId);
      if (chatRoomRes.success && chatRoomRes.data?.id) {
        setRoomId(chatRoomRes.data.id);
        setChatLoading(false);
      }
    } catch (err) {
      console.error("❌ LOG: Error in fetchRoomOnInit:", err);
      setChatLoading(false);
    }
  };

  // Fetch messages with pagination
  const fetchMessages = async (roomId, requestedPage = 1) => {
    if (
      isLoadingMessages ||
      !hasMoreMessages ||
      (lastPage && requestedPage > lastPage)
    )
      return;

    const container = messagesContainerRef.current;
    const previousScrollHeight = container?.scrollHeight ?? 0;

    setIsLoadingMessages(true);

    try {
      const res = await getMessagesByRoomId(roomId, requestedPage);
      if (res.success) {
        const newMessages = res.data;

        if (newMessages.length === 0) {
          setHasMoreMessages(false);
          if (requestedPage === 1) {
            setIsStartMessage(true);
          }
        } else {
          setMessages((prev) => [...prev, ...newMessages]);
          setPage((prev) => prev + 1);
          setLastPage(res.meta.last_page);

          setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - previousScrollHeight;
          }, 100);
        }
      }
    } catch (err) {
      console.error("❌ Error fetching messages:", err);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Handle infinite scroll for messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop === 0 && !isLoadingMessages && hasMoreMessages) {
        fetchMessages(roomId, page);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [roomId, page, isLoadingMessages, hasMoreMessages]);

  // Handle file input change
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Normalize incoming message format
  const normalizeIncoming = (msg) => ({
    id: msg.id,
    room_id: msg.room_id || msg.chat_room_id,
    chat_room_id: msg.chat_room_id || msg.room_id,
    sender_id: msg.sender_id,
    sender_type: msg.sender_type || "admin",
    sender_name: msg.sender_name,
    body: msg.body ?? msg.message ?? "",
    message_type: msg.message_type || (msg.attachment_url ? "file" : "text"),
    attachment_url: msg.attachment_url || null,
    created_at: msg.created_at || new Date().toISOString(),
  });

  // Handle sending messages
  const handleSend = async () => {
    if (!roomId) {
      return;
    }

    const formData = new FormData();
    formData.append("room_id", roomId);
    formData.append("sender_id", user.id);

    if (file) {
      const type = file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
          ? "video"
          : "file";
      formData.append("file", file);
      formData.append("attachment_type", type);
    } else if (text.trim()) {
      formData.append("message", text.trim());
    } else {
      console.warn("⚠️ LOG: Empty message and no file");
      return;
    }

    try {
      const response = await sendMessage(formData, true);

      if (response.success && response.data) {

        const savedMsg = normalizeIncoming(response.data);
        emitSocketMessage(savedMsg);
        setText("");
        setFile(null);
      } else {
        notify.error(
          "❌ LOG: Failed to send message:",
          response.message || "Unknown error"
        );
      }
    } catch (err) {
      notify.error(
        "❌ LOG: Error sending message:",
        err.response?.data?.message || "Unknown error"
      );
    }
  };

  // Extract file name from URL
  const getFileName = (url) => {
    try {
      const decoded = decodeURIComponent(url);
      const parts = decoded.split("/");
      for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i].includes(".")) return parts[i].split("?")[0];
      }
      return "File";
    } catch {
      return "File";
    }
  };

  // Format timestamp for display
  const formatSmartTime = (timestamp) => {
    const date = new Date(timestamp);
    const time = format(date, "hh:mm a");

    if (isToday(date)) return `Today at ${time}`;
    if (isYesterday(date)) return `Yesterday at ${time}`;
    return `${format(date, "dd MMM")} at ${time}`;
  };

  // Group messages by date for display
  const groupMessagesByDate = (msgs) => {
    const groups = [];
    let lastDate = null;

    msgs.forEach((msg) => {
      const msgDate = new Date(msg.created_at);

      if (!lastDate || !isSameDay(lastDate, msgDate)) {
        let label = "";
        if (isToday(msgDate)) label = "Today";
        else if (isYesterday(msgDate)) label = "Yesterday";
        else label = format(msgDate, "dd MMM yyyy");

        groups.push({ type: "divider", label });
        lastDate = msgDate;
      }

      groups.push({ type: "message", data: msg });
    });

    return groups;
  };

  // Handle message deletion
  const handleDeleteMessage = async (messageId) => {
    try {
      const response = await deleteMessage(messageId);
      if (response.success) {
        notify.success("Message deleted");
        fetchMessages(roomId, page);
      } else {
        notify.error("Failed to delete message");
      }
    } catch (error) {
      console.error("Failed to delete message:", error);
      notify.error(error.response.data.message);
    }
  };

  // Handle chatbox close
  const handleClose = () => {
    setIsOpen(false);
    setPage(1);
    setMessages([]);
    setHasMoreMessages(true);
    setLastPage(1);
    dispatch(toggleChatBox(false));
  };

  return (
    <>
      {/* Chat FAB button */}
      {!isOpen && (
        <div className="chat-fab" onClick={() => setIsOpen(true)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="38"
            height="38"
            viewBox="0 0 24 24"
          >
            <path
              fill="#fff"
              d="M18 3a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4h-4.724l-4.762 2.857a1 1 0 0 1-1.508-.743L7 21v-2H6a4 4 0 0 1-3.995-3.8L2 15V7a4 4 0 0 1 4-4zm-2.8 9.286a1 1 0 0 0-1.414.014a2.5 2.5 0 0 1-3.572 0a1 1 0 0 0-1.428 1.4a4.5 4.5 0 0 0 6.428 0a1 1 0 0 0-.014-1.414M9.51 8H9.5a1 1 0 1 0 0 2h.01a1 1 0 0 0 0-2m5 0h-.01a1 1 0 0 0 0 2h.01a1 1 0 0 0 0-2"
            />
          </svg>
        </div>
      )}
      {/* Chat Popup */}
      {isOpen && (
        <div className="chat-popup open" ref={chatBoxRef}>
          <div className="chat-header">
            <span>Chat with Coach Paul</span>
            <button className="close-btn" onClick={handleClose}>
              <CloseIcon />
            </button>
          </div>
          {chatLoading ? (
            <ChatShimmer count={6} />
          ) : (
            <>
              {!roomId ? (
                <div className="chat-empty-state">
                  <p>Please subscribe to a plan to connect with Coach Paul.</p>
                </div>
              ) : !chatEnabled ? (
                <div className="chat-empty-state">
                  <p>Chat is not enabled for this Subscription Purhase.</p>
                </div>
              ) : (
                <div className="chat-messages" ref={messagesContainerRef}>
                  {isLoadingMessages && hasMoreMessages ? (
                    <ChatShimmer count={3} />
                  ) : (
                    <>
                      {isStartMessage && (
                        <div className="chat-start-message">
                          <p>
                            Welcome to Recompplus Fitness! Start your journey with
                            us today!
                          </p>
                        </div>
                      )}
                      {groupMessagesByDate([...messages].reverse()).map(
                        (item, idx) => {
                          if (item.type === "divider") {
                            return (
                              <div key={idx} className="chat-date-divider">
                                <span>{item.label}</span>
                              </div>
                            );
                          }
                          const msg = item.data;
                          const isClient = msg.sender_type === "client";
                          const isFile = msg.message_type === "file";
                          const isImage = msg.message_type === "image";
                          const isVideo = msg.message_type === "video";
                          const msgReactions = reactions[msg.id] || (msg.emoji ? [msg.emoji] : []);
                          const isDeleted = msg.is_message_delete;

                          return (
                            <div
                              key={msg.id}
                              className={`msg ${isClient ? "client" : "admin"}`}
                            >
                              <div className="msg-header">
                                <button
                                  className="msg-menu-btn"
                                  onClick={() =>
                                    setHoveredMsgId((prev) =>
                                      prev === msg.id ? null : msg.id
                                    )
                                  }
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 32 32"
                                  >
                                    <path
                                      fill="#888"
                                      d="M16 6a1.999 1.999 0 1 0 0 4a1.999 1.999 0 1 0 0-4m0 8a1.999 1.999 0 1 0 0 4a1.999 1.999 0 1 0 0-4m0 8a1.999 1.999 0 1 0 0 4a1.999 1.999 0 1 0 0-4"
                                    />
                                  </svg>
                                </button>
                                {hoveredMsgId === msg.id && (
                                  <div className="msg-dropdown" ref={dropdownRef}>
                                    <div
                                      className="msg-dropdown-item"
                                      onClick={() => {
                                        setShowReactionPicker(msg.id);
                                        setShowInputEmojiPicker(false);
                                        setHoveredMsgId(null);
                                      }}
                                    >
                                      😊 React
                                    </div>
                                    <div
                                      className="msg-dropdown-item"
                                      onClick={() => {
                                        handleDeleteMessage(msg.id);
                                      }}
                                    >
                                      🗑️ Delete
                                    </div>
                                  </div>
                                )}
                              </div>

                              {isDeleted ? (
                                <i>This message was deleted...</i>
                              ) : isFile ? (
                                <a
                                  href={msg.attachment_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="message-file"
                                >
                                  {getFileName(msg.attachment_url)}
                                </a>
                              ) : isImage ? (
                                <img
                                  src={msg.attachment_url}
                                  alt="sent"
                                  className="preview-image"
                                />
                              ) : isVideo ? (
                                <video controls className="preview-video">
                                  <source src={msg.attachment_url} />
                                </video>
                              ) : (
                                msg.body
                              )}

                              {msgReactions.length > 0 && (
                                <div className="message-reaction">
                                  {msgReactions.map((emoji, idx) => (
                                    <span
                                      key={idx}
                                      onClick={() => {
                                        setShowReactionPicker(msg.id);
                                        setShowInputEmojiPicker(false);
                                        setHoveredMsgId(null);
                                      }}
                                      style={{ cursor: "pointer" }}
                                    >
                                      {emoji}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {showReactionPicker === msg.id && (
                                <div
                                  ref={reactionPickerRef}
                                  className="reaction-picker"
                                  style={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    marginBottom: '8px',
                                    zIndex: 1001,
                                    left: isClient ? 'auto' : '0',
                                    right: isClient ? '0' : 'auto',
                                    transform: isClient ? 'translateX(0)' : 'translateX(0)'
                                  }}
                                >
                                  <EmojiPicker
                                    onEmojiClick={(emojiData) =>
                                      handleEmojiReaction(emojiData, msg?.id)
                                    }
                                    height={300}
                                    width={280}
                                    searchDisabled={false}
                                    skinTonesDisabled={true}
                                    previewConfig={{
                                      showPreview: false
                                    }}
                                  />
                                </div>
                              )}

                              <div className="chat-meta">
                                {formatSmartTime(msg.created_at)}
                                {isClient && msg.seen && (
                                  <span className="seen-indicator">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                    >
                                      <path
                                        fill="none"
                                        stroke="#007BFF"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="1.5"
                                        d="m1.75 9.75 2.5 2.5m3.5-4 2.5-2.5m-4.5 4 2.5 2.5 6-6.5"
                                      />
                                    </svg>
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {file && (
                <div className="file-preview-bar">
                  <div className="file-preview-content">
                    {file.type.startsWith("image/") && (
                      <img
                        src={URL.createObjectURL(file)}
                        alt="preview"
                        className="file-preview-img"
                      />
                    )}
                    {file.type.startsWith("video/") && (
                      <video
                        controls
                        src={URL.createObjectURL(file)}
                        className="file-preview-video"
                      />
                    )}
                    {!file.type.startsWith("image/") &&
                      !file.type.startsWith("video/") && (
                        <div className="file-preview-fallback">
                          📎 {file.name}
                        </div>
                      )}
                    <button
                      className="file-remove-btn"
                      onClick={() => setFile(null)}
                    >
                      <CloseIcon style={{ color: "black" }} />
                    </button>
                  </div>
                </div>
              )}

              <div className="chat-input-wrapper">
                <div className="chat-input-inner">
                  <label
                    htmlFor="client-file-upload"
                    className="plus-icon"
                    style={{
                      pointerEvents: chatEnabled ? "auto" : "none",
                      opacity: chatEnabled ? 1 : 0.5
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="none"
                        stroke="#fff"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 15V9a6 6 0 1 1 12 0v8a4 4 0 1 1-8 0V9a2 2 0 1 1 4 0v8"
                      />
                    </svg>
                  </label>
                  <input
                    id="client-file-upload"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    disabled={!chatEnabled}
                  />
                  <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={
                      chatEnabled
                        ? "Type your message..."
                        : "Chat is not enabled for this subscription"
                    }
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    className="chat-text-field"
                    disabled={!chatEnabled}
                  />
                  <button
                    className="emoji-btn"
                    disabled={!chatEnabled}
                    onClick={() => {
                      setShowInputEmojiPicker((prev) => !prev);
                      setShowReactionPicker(null);
                      setHoveredMsgId(null);
                    }}
                    style={{ opacity: chatEnabled ? 1 : 0.5 }}
                  >
                    😄
                  </button>

                  {showInputEmojiPicker && (
                    <div className="emoji-picker-container" ref={inputEmojiRef}>
                      <EmojiPicker
                        onEmojiClick={(emojiData) =>
                          setText((prev) => prev + emojiData.emoji)
                        }
                        height={300}
                        width={280}
                        searchDisabled={false}
                        skinTonesDisabled={true}
                        previewConfig={{
                          showPreview: false
                        }}
                      />
                    </div>
                  )}
                </div>
                <button
                  className="chat-send-btn"
                  onClick={handleSend}
                  disabled={!chatEnabled}
                  style={{ opacity: chatEnabled ? 1 : 0.5 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#10b981"
                      fillRule="evenodd"
                      d="M3.291 3.309a.75.75 0 0 0-.976.996l3.093 6.945H13a.75.75 0 0 1 0 1.5H5.408l-3.093 6.945a.75.75 0 0 0 .976.996l19-8a.75.75 0 0 0 0-1.382z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

// Export the ClientChatBox component
export default ClientChatBox;